import { useSnackbar } from 'notistack';
import React from 'react';
import { errorSnackbar } from '../../../../../components/ErrorSnackbar';
import { WizardStep } from '../../../../../components/Wizard/WizardStep';
import { type OnSubmit, useWizard } from '../../../../../design-system';
import { type JobLocationDetails, jobLocationFields } from '../../../../../utils/jobs/location';
import { locationValidationSchema } from '../../../../../utils/locations/FieldDefinitions';
import type { JobWizardState } from '../types';

export const LocationStep: React.FC = () => {
  const { state: untypedState, setState: untypedSetState, prevStep, nextStep, step } = useWizard();
  const state = untypedState as Partial<JobWizardState>;
  const setState = untypedSetState as (state: Partial<JobWizardState>) => void;
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const locationName = state.driverFieldContents?.name ? `${state.driverFieldContents.name}'s Home` : '';
  const initialValues: JobLocationDetails = state.locationFieldContents
    ? { ...state.locationFieldContents, name: locationName }
    : {
        name: locationName,
        address: { address1: '', address2: '', address3: '', city: '', postCode: '', state: '', country: '' },
        coordinates: { latitude: '', longitude: '' },
        timeZone: '',
        installationPoint: null,
      };

  const onCancel = React.useCallback<OnSubmit<JobLocationDetails>>(
    (values) => {
      setState({ locationFieldContents: values });
      prevStep();
    },
    [setState, prevStep],
  );

  const onSubmit = React.useCallback<OnSubmit<JobLocationDetails>>(
    async (values, { setSubmitting, setFieldError }) => {
      setSubmitting(true);
      try {
        setFieldError('email', undefined);
        setState({ locationFieldContents: values });
        nextStep();
      } catch (e) {
        setSubmitting(false);
        errorSnackbar('Unable to create location', closeSnackbar, enqueueSnackbar, e);
      }
    },
    [closeSnackbar, enqueueSnackbar, nextStep, setState],
  );

  return (
    <WizardStep<JobLocationDetails>
      fields={Object.values(jobLocationFields({ organisationId: state.organisation?.id, timeZoneEditable: true }))}
      initialValues={initialValues}
      name="Location details"
      onCancel={onCancel}
      onSubmit={onSubmit}
      step={step}
      subtitle="Enter the location where the driver's new charge point will be installed."
      validationSchema={locationValidationSchema}
    />
  );
};
